import {
  EexLiveUniverseResponseSchema,
  EexLiveUniverseRollingPolicyResponseSchema,
  EexLiveUniverseRollingPolicyUpdateRequestSchema,
  EexLiveUniverseUpdateRequestSchema,
  type EexLiveUniverseResponse,
  type EexLiveUniverseRollingLadder,
  type EexLiveUniverseRollingOverride,
  type EexLiveUniverseRollingPolicyResponse,
} from "@chro/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckSquare, Search, Settings2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { apiContract } from "../../../lib/api";
import {
  eexRollingLadderKey,
  sameEexRollingDraft,
  seedEexRollingLadders,
  seedEexRollingOverrides,
} from "../../../lib/eex-live-universe";
import { eexNaturalGasFuturesPresetSubjectIds } from "../../../lib/eex-live-market";

type LiveUniverseMode = EexLiveUniverseResponse["mode"];
type LiveUniverseItem = EexLiveUniverseResponse["items"][number];
type RollingStrategy = EexLiveUniverseRollingPolicyResponse["strategy"];
type RollingSeries = EexLiveUniverseRollingPolicyResponse["series"][number];
type RollingAvailability = RollingSeries["available"][number];
type RollingResolutionFailureReason = NonNullable<EexLiveUniverseRollingPolicyResponse["resolutionFailureReason"]>;

type ProductGroup = {
  key: string;
  label: string;
  commodityCode: string;
  areaCode: string;
  items: LiveUniverseItem[];
};

const MAX_COMPANY_LIVE_UNIVERSE_SELECTION = 20_000;
const MAX_ROLLING_LADDERS = 3_500;
const MAX_ROLLING_OVERRIDES = 20_000;
const ROLLING_POLICY_PATH = "/eex/market-data/preferences/live-universe/rolling-policy";
const EXACT_UNIVERSE_QUERY_KEY = ["eex-market-data", "company-live-universe"] as const;
const ROLLING_POLICY_QUERY_KEY = ["eex-market-data", "company-live-universe-rolling-policy"] as const;

export function CompanyLiveUniversePanel() {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [mode, setMode] = useState<LiveUniverseMode>("legacy_scopes");
  const [strategy, setStrategy] = useState<RollingStrategy>("exact");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [ladders, setLadders] = useState<EexLiveUniverseRollingLadder[]>([]);
  const [overrides, setOverrides] = useState<Map<string, EexLiveUniverseRollingOverride["action"]>>(new Map());
  const [search, setSearch] = useState("");
  const [loadedVersion, setLoadedVersion] = useState<number | null>(null);
  const [loadedPolicyVersion, setLoadedPolicyVersion] = useState<number | null>(null);
  const [policyRefreshPending, setPolicyRefreshPending] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const draft = { mode, strategy, selected, ladders, overrides };
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const ownsDraft = (submitted: typeof draft | undefined) => submitted != null &&
    submitted.mode === draftRef.current.mode && submitted.strategy === draftRef.current.strategy &&
    submitted.selected === draftRef.current.selected && submitted.ladders === draftRef.current.ladders &&
    submitted.overrides === draftRef.current.overrides;
  const universeQ = useQuery({
    queryKey: EXACT_UNIVERSE_QUERY_KEY,
    queryFn: () => apiContract(
      EexLiveUniverseResponseSchema,
      "/eex/market-data/preferences/live-universe"
    ),
    enabled: expanded,
    retry: false,
  });
  const policyQ = useQuery({
    queryKey: ROLLING_POLICY_QUERY_KEY,
    queryFn: () => apiContract(EexLiveUniverseRollingPolicyResponseSchema, ROLLING_POLICY_PATH),
    enabled: expanded,
    retry: false,
  });
  const universe = universeQ.data;
  const policy = policyQ.data;

  useEffect(() => {
    // Both endpoints share one optimistic version. Initialize their drafts
    // atomically so a stale response cannot revive an older version after the
    // other strategy has just been saved.
    if (!universe || !policy || universe.version !== policy.version) return;
    if (loadedVersion !== null || loadedPolicyVersion !== null) return;
    setMode(universe.mode);
    setSelected(new Set(universe.items.filter((item) => item.selected).map((item) => item.subjectId)));
    setLoadedVersion(universe.version);
    setStrategy(policy.strategy);
    setLadders([...policy.ladders]);
    setOverrides(new Map(policy.overrides.map((override) => [override.subjectId, override.action])));
    setLoadedPolicyVersion(policy.version);
  }, [loadedPolicyVersion, loadedVersion, policy, universe]);

  const exactSaveM = useMutation({
    onMutate: () => { setSaveError(null); return draftRef.current; },
    mutationFn: () => apiContract(
      EexLiveUniverseResponseSchema,
      "/eex/market-data/preferences/live-universe",
      {
        method: "PUT",
        body: JSON.stringify(EexLiveUniverseUpdateRequestSchema.parse(mode === "legacy_scopes"
          ? { mode, expectedVersion: loadedVersion ?? universe?.version ?? 0 }
          : { mode, expectedVersion: loadedVersion ?? universe?.version ?? 0, subjectIds: [...selected] })),
      }
    ),
    onSuccess: (result, _variables, submitted) => {
      const reconciliation = result.reconciliation;
      setFeedback(reconciliation?.activationQueueWarning
        ? "Selection saved. Feed activation is queued for the recurring worker because the immediate wake-up was delayed."
        : result.mode === "company_watchlist"
          ? `Exact selection saved. ${reconciliation?.activatedAreaFeeds ?? 0} area feed(s) activated and ${reconciliation?.deactivatedAreaFeeds ?? 0} deactivated.`
          : "Legacy scope ownership saved. Existing scope states were preserved.");
      if (ownsDraft(submitted)) {
        setMode(result.mode);
        setStrategy("exact");
        setSelected(new Set(result.items.filter((item) => item.selected).map((item) => item.subjectId)));
        setLadders([]);
        setOverrides(new Map());
      }
      setLoadedVersion(result.version);
      setLoadedPolicyVersion(result.version);
      setPolicyRefreshPending(true);
      queryClient.setQueryData(EXACT_UNIVERSE_QUERY_KEY, result);
      queryClient.setQueryData<EexLiveUniverseRollingPolicyResponse>(ROLLING_POLICY_QUERY_KEY, (current) => current
        ? { ...current, strategy: "exact", version: result.version, ladders: [], overrides: [] }
        : current);
      void queryClient.invalidateQueries({ queryKey: ["eex-market-data"] })
        .finally(() => setPolicyRefreshPending(false));
    },
    onError: (error) => setSaveError(error),
  });

  const rollingSaveM = useMutation({
    onMutate: () => { setSaveError(null); return draftRef.current; },
    mutationFn: () => apiContract(
      EexLiveUniverseRollingPolicyResponseSchema,
      ROLLING_POLICY_PATH,
      {
        method: "PUT",
        body: JSON.stringify(EexLiveUniverseRollingPolicyUpdateRequestSchema.parse({
          expectedVersion: loadedPolicyVersion ?? policy?.version ?? 0,
          ladders,
          overrides: overrideRows(overrides),
        })),
      }
    ),
    onSuccess: (result, _variables, submitted) => {
      if (ownsDraft(submitted)) {
        setMode("company_watchlist");
        setStrategy(result.strategy);
        setLadders([...result.ladders]);
        setOverrides(new Map(result.overrides.map((override) => [override.subjectId, override.action])));
      }
      setLoadedPolicyVersion(result.version);
      setLoadedVersion(result.version);
      const selectedSubjectIds = new Set(result.memberships
        .filter((membership) => membership.selected)
        .map((membership) => membership.subjectId));
      if (ownsDraft(submitted)) setSelected(selectedSubjectIds);
      setFeedback(result.resolution.deficits.length > 0
        ? `Rolling policy saved with ${result.resolution.deficits.length} ladder deficit(s); available contracts remain selected safely.`
        : `Rolling policy saved. ${result.resolution.selectedInstruments} instruments are currently selected.`);
      queryClient.setQueryData(ROLLING_POLICY_QUERY_KEY, result);
      queryClient.setQueryData<EexLiveUniverseResponse>(EXACT_UNIVERSE_QUERY_KEY, (current) => current
        ? {
            ...current,
            configured: true,
            mode: "company_watchlist",
            version: result.version,
            items: current.items.map((item) => ({ ...item, selected: selectedSubjectIds.has(item.subjectId) })),
            summary: {
              ...current.summary,
              selectedInstruments: result.resolution.selectedInstruments,
            },
          }
        : current);
      void queryClient.invalidateQueries({ queryKey: ["eex-market-data"] });
    },
    onError: (error) => setSaveError(error),
  });

  const groups = useMemo(() => groupItems(universe?.items ?? [], search), [search, universe?.items]);
  const visibleIds = groups.flatMap((group) => group.items.map((item) => item.subjectId));
  const naturalGasFuturesPresetIds = useMemo(
    () => eexNaturalGasFuturesPresetSubjectIds(universe?.items ?? []),
    [universe?.items]
  );
  const selectedAreas = useMemo(() => new Set((universe?.items ?? [])
    .filter((item) => item.selected)
    .map((item) => `${item.commodityCode}\u0000${item.areaCode}`)).size, [universe?.items]);

  const summary = (
    <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3">
      <span className="flex items-center gap-2 text-sm font-semibold">
        <Settings2 size={16} /> Company live universe
        <span className="text-xs font-normal text-muted">Configure acquisition</span>
      </span>
      <span className="text-right text-xs text-muted">
        {universe
          ? `${universe.summary.selectedInstruments} instruments · ${selectedAreas} area feeds · ${policy?.strategy ?? "exact"} · version ${policy?.version ?? universe.version}`
          : "Open to load eligible instruments"}
      </span>
    </summary>
  );

  const configurationVersionsMatch = Boolean(universe && policy && universe.version === policy.version);
  if (!expanded || universeQ.isLoading || policyQ.isLoading || universeQ.isError || policyQ.isError
    || !universe || !policy || !configurationVersionsMatch
    || loadedVersion === null || loadedPolicyVersion === null) {
    return (
      <details
        id="eex-company-live-universe"
        open={expanded}
        onToggle={(event) => setExpanded(event.currentTarget.open)}
        className="rounded-lg border border-border p-4"
      >
        {summary}
        {expanded && (
          <div className={universeQ.isError || policyQ.isError ? "mt-4 text-sm text-negRed" : "mt-4 text-sm text-muted"}>
            {universeQ.isError || policyQ.isError
              ? `Unable to load the company live-universe configuration: ${((universeQ.error ?? policyQ.error) as Error)?.message ?? "unknown error"}`
              : universe && policy && !configurationVersionsMatch
                ? "The exact and rolling configuration versions are still synchronizing. Reload this panel before editing."
                : "Loading company live universe…"}
          </div>
        )}
      </details>
    );
  }

  const policyOverrides = policy?.overrides ?? [];
  const dirty = mode === "legacy_scopes"
    ? mode !== universe.mode
    : strategy === "exact"
      ? mode !== universe.mode
        || policy?.strategy !== "exact"
        || !sameSet(selected, new Set(universe.items.filter((item) => item.selected).map((item) => item.subjectId)))
      : mode !== universe.mode
        || policy?.strategy !== "rolling"
        || !sameEexRollingDraft(ladders, overrideRows(overrides), policy?.ladders ?? [], policyOverrides);
  const selectionTooLarge = strategy === "exact" && selected.size > MAX_COMPANY_LIVE_UNIVERSE_SELECTION;
  const rollingPolicyTooLarge = strategy === "rolling"
    && (ladders.length > MAX_ROLLING_LADDERS || overrides.size > MAX_ROLLING_OVERRIDES);
  const savePending = exactSaveM.isPending || rollingSaveM.isPending;
  const exactWouldBeEmpty = mode === "company_watchlist"
    && strategy === "exact"
    && selected.size === 0;
  const rollingWouldBeEmpty = mode === "company_watchlist"
    && strategy === "rolling"
    && ladders.length === 0;

  return (
    <details
      id="eex-company-live-universe"
      open={expanded}
      onToggle={(event) => setExpanded(event.currentTarget.open)}
      className="rounded-lg border border-border p-4"
    >
      {summary}

      <div className="mt-4 space-y-4">
        <p className="max-w-5xl text-xs text-muted">
          This versioned company policy controls the shared live universe. EEX readings are requested once per
          commodity and area, so rolling more delivery periods inside an active area does not create more API requests.
          Personal stars and each user&apos;s board layout never change acquisition. Configure board is display-only. Official
          settlement coverage remains separate and runs after its configured publication cutoff.
        </p>

        {!universe.configured && (
          <div className="rounded border border-accent/40 bg-accent/5 p-2 text-xs text-accent">
            Existing live scopes remain authoritative until you explicitly save this form. Opening this panel cannot
            turn off the current feed.
          </div>
        )}

        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="eex-live-universe-mode"
              checked={mode === "legacy_scopes"}
              onChange={() => setMode("legacy_scopes")}
            />
            Preserve legacy scopes
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="eex-live-universe-mode"
              checked={mode === "company_watchlist"}
              onChange={() => setMode("company_watchlist")}
            />
            Use company policy
          </label>
        </div>

        {mode === "legacy_scopes" ? (
          <div className="rounded border border-border bg-panel/40 p-3 text-xs text-muted">
            Cascena will preserve the established enabled scope states. Switch to company policy when this versioned
            selection should become the source of truth for paired live top-of-book and public-trade feeds.
          </div>
        ) : (
          <>
            <div className="flex flex-wrap gap-4 rounded border border-border bg-panel/30 p-3 text-sm">
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="radio"
                  name="eex-live-universe-strategy"
                  checked={strategy === "exact"}
                  onChange={() => setStrategy("exact")}
                />
                <span><span className="block font-medium">Exact contracts</span><span className="block text-xs text-muted">Keep a fixed list of provider instruments.</span></span>
              </label>
              <label className="flex cursor-pointer items-start gap-2">
                <input
                  type="radio"
                  name="eex-live-universe-strategy"
                  checked={strategy === "rolling"}
                  disabled={!policy || policyRefreshPending || policyQ.isFetching}
                  onChange={() => {
                    if (!policy) return;
                    if (ladders.length === 0 && overrides.size === 0) {
                      setLadders(seedEexRollingLadders(policy.series));
                      setOverrides(new Map(seedEexRollingOverrides(
                        policy.memberships,
                        new Set(universe.items.map((item) => item.subjectId))
                      ).map((override) => [override.subjectId, override.action])));
                    }
                    setStrategy("rolling");
                  }}
                />
                <span><span className="block font-medium">Rolling ladder</span><span className="block text-xs text-muted">Continuously select configurable nearest contracts as the catalogue advances.</span></span>
              </label>
              {(policyRefreshPending || policyQ.isFetching) && <span className="self-center text-xs text-muted">Refreshing rolling choices…</span>}
            </div>

            {strategy === "exact" ? (
              <ExactUniverseEditor
                groups={groups}
                visibleIds={visibleIds}
                selected={selected}
                setSelected={setSelected}
                search={search}
                setSearch={setSearch}
                naturalGasFuturesPresetIds={naturalGasFuturesPresetIds}
              />
            ) : policy ? (
              <RollingUniverseEditor
                policy={policy}
                universeItems={universe.items}
                ladders={ladders}
                setLadders={setLadders}
                overrides={overrides}
                setOverrides={setOverrides}
              />
            ) : null}
          </>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={!dirty || savePending || selectionTooLarge || rollingPolicyTooLarge
              || rollingWouldBeEmpty || (strategy === "rolling" && !policy)}
            onClick={() => {
              if (exactWouldBeEmpty && !confirm("Save an empty company universe and stop all paired live area feeds?")) return;
              setFeedback(null);
              if (mode === "company_watchlist" && strategy === "rolling") rollingSaveM.mutate();
              else exactSaveM.mutate();
            }}
            className="inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-sm text-bg disabled:opacity-40"
          >
            <CheckSquare size={14} /> {savePending ? "Saving…" : "Save company universe"}
          </button>
          {feedback && <span className="text-xs text-posGreen">{feedback}</span>}
          {selectionTooLarge && (
            <span className="text-xs text-negRed">
              Select at most {MAX_COMPANY_LIVE_UNIVERSE_SELECTION.toLocaleString()} instruments before saving.
            </span>
          )}
          {rollingPolicyTooLarge && (
            <span className="text-xs text-negRed">
              Use at most {MAX_ROLLING_LADDERS.toLocaleString()} ladders and {MAX_ROLLING_OVERRIDES.toLocaleString()} contract exceptions.
            </span>
          )}
          {rollingWouldBeEmpty && (
            <span className="text-xs text-negRed">
              Add at least one rolling ladder before saving; contract exceptions modify a ladder policy.
            </span>
          )}
          {saveError && (
            <span className="text-xs text-negRed">
              {(saveError as Error).message.includes("version_conflict")
                ? "This configuration changed elsewhere. Reload the page and review the latest version."
                : `Unable to save: ${(saveError as Error).message}`}
            </span>
          )}
        </div>
      </div>
    </details>
  );
}

function ExactUniverseEditor({
  groups,
  visibleIds,
  selected,
  setSelected,
  search,
  setSearch,
  naturalGasFuturesPresetIds,
}: {
  groups: ProductGroup[];
  visibleIds: string[];
  selected: Set<string>;
  setSelected: Dispatch<SetStateAction<Set<string>>>;
  search: string;
  setSearch: (value: string) => void;
  naturalGasFuturesPresetIds: string[];
}) {
  return <>
    <div className="flex flex-wrap items-center gap-2">
      <label className="relative min-w-64 flex-1">
        <Search size={14} className="absolute left-2 top-2 text-muted" />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search product, area or delivery period"
          className="w-full rounded border border-border bg-transparent py-1.5 pl-7 pr-2 text-sm focus:border-accent focus:outline-none"
        />
      </label>
      <button type="button" onClick={() => setSelected((current) => new Set([...current, ...visibleIds]))} className="rounded border border-border px-2.5 py-1.5 text-xs">Select shown</button>
      <button
        type="button"
        onClick={() => setSelected((current) => {
          const next = new Set(current);
          for (const id of visibleIds) next.delete(id);
          return next;
        })}
        className="rounded border border-border px-2.5 py-1.5 text-xs"
      >Clear shown</button>
      <button
        type="button"
        disabled={naturalGasFuturesPresetIds.length === 0}
        onClick={() => setSelected((current) => new Set([...current, ...naturalGasFuturesPresetIds]))}
        className="rounded border border-accent/50 px-2.5 py-1.5 text-xs text-accent disabled:opacity-40"
        title="Add outright EEX natural-gas futures without removing the current company selection"
      >Add TTF + CEGH futures ({naturalGasFuturesPresetIds.length})</button>
    </div>

    <div className="max-h-[32rem] overflow-auto rounded border border-border">
      {groups.length === 0 ? (
        <div className="p-4 text-sm text-muted">No eligible futures or spreads match this search.</div>
      ) : groups.map((group) => {
        const groupIds = group.items.map((item) => item.subjectId);
        const selectedCount = groupIds.filter((id) => selected.has(id)).length;
        return (
          <div key={group.key} className="border-b border-border last:border-b-0">
            <label className="sticky top-0 z-10 flex cursor-pointer items-center gap-2 bg-panel px-3 py-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={selectedCount === groupIds.length}
                ref={(element) => {
                  if (element) element.indeterminate = selectedCount > 0 && selectedCount < groupIds.length;
                }}
                onChange={(event) => setSelected((current) => {
                  const next = new Set(current);
                  for (const id of groupIds) event.target.checked ? next.add(id) : next.delete(id);
                  return next;
                })}
              />
              <span>{group.label}</span>
              <span className="text-xs font-normal text-muted">{group.commodityCode} · {group.areaCode} · {selectedCount}/{groupIds.length}</span>
            </label>
            <div className="grid gap-px bg-border sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {group.items.map((item) => (
                <label key={item.subjectId} className="flex cursor-pointer items-start gap-2 bg-bg px-3 py-2 text-xs hover:bg-panel/70" title={`${item.displayName ?? item.providerKey}\n${item.providerKey}`}>
                  <input
                    type="checkbox"
                    checked={selected.has(item.subjectId)}
                    onChange={(event) => setSelected((current) => {
                      const next = new Set(current);
                      event.target.checked ? next.add(item.subjectId) : next.delete(item.subjectId);
                      return next;
                    })}
                  />
                  <span><span className="block font-medium">{item.deliveryPeriod.label}</span><span className="block truncate text-muted">{item.displayName ?? item.providerKey}</span></span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  </>;
}

function RollingUniverseEditor({
  policy,
  universeItems,
  ladders,
  setLadders,
  overrides,
  setOverrides,
}: {
  policy: EexLiveUniverseRollingPolicyResponse;
  universeItems: LiveUniverseItem[];
  ladders: EexLiveUniverseRollingLadder[];
  setLadders: Dispatch<SetStateAction<EexLiveUniverseRollingLadder[]>>;
  overrides: Map<string, EexLiveUniverseRollingOverride["action"]>;
  setOverrides: Dispatch<SetStateAction<Map<string, EexLiveUniverseRollingOverride["action"]>>>;
}) {
  const [seriesSearch, setSeriesSearch] = useState("");
  const [exceptionSearch, setExceptionSearch] = useState("");
  const ladderByKey = new Map(ladders.map((ladder) => [eexRollingLadderKey(ladder), ladder]));
  const membershipBySubject = new Map(policy.memberships.map((membership) => [membership.subjectId, membership]));
  const seriesNeedle = seriesSearch.trim().toLocaleLowerCase();
  const visibleSeries = policy.series.filter((series) => !seriesNeedle || [
    series.seriesLabel,
    series.commodityCode,
    series.areaCode,
    series.profile,
    series.familyCode,
  ].filter(Boolean).join(" ").toLocaleLowerCase().includes(seriesNeedle));
  const exceptionNeedle = exceptionSearch.trim().toLocaleLowerCase();
  const universeSubjectIds = new Set(universeItems.map((item) => item.subjectId));
  const unavailableOverrideIds = [...overrides.keys()].filter((subjectId) =>
    !universeSubjectIds.has(subjectId)
    && (!exceptionNeedle || subjectId.toLocaleLowerCase().includes(exceptionNeedle))
  );
  const exceptionItems = universeItems.filter((item) => {
    if (!exceptionNeedle) return overrides.has(item.subjectId);
    return [item.deliveryPeriod.label, item.displayName, item.providerKey, item.productGroup.label, item.areaCode]
      .filter(Boolean).join(" ").toLocaleLowerCase().includes(exceptionNeedle);
  }).slice(0, 200);

  const replaceLadder = (
    series: RollingSeries,
    availability: RollingAvailability,
    update: Partial<Pick<EexLiveUniverseRollingLadder, "selectionRule" | "targetCount">> | null
  ) => setLadders((current) => {
    const key = `${series.seriesKey}\u0000${availability.deliveryKind}`;
    const without = current.filter((ladder) => eexRollingLadderKey(ladder) !== key);
    if (update == null) return without;
    const selectionRule = update.selectionRule ?? "nearest_n";
    return [...without, {
      seriesKey: series.seriesKey,
      seriesLabel: series.seriesLabel,
      commodityCode: series.commodityCode,
      areaCode: series.areaCode,
      profile: series.profile,
      familyCode: series.familyCode,
      currency: series.currency,
      uom: series.uom,
      deliveryKind: availability.deliveryKind,
      selectionRule,
      targetCount: selectionRule === "all_active"
        ? null
        : Math.max(1, Math.min(5_000, update.targetCount ?? (availability.selectedCount || 1))),
    }];
  });

  return <div className="space-y-3">
    <RollingResolutionWarning
      strategy={policy.strategy}
      resolutionStatus={policy.resolutionStatus}
      resolutionFailureReason={policy.resolutionFailureReason}
      lastResolutionAttemptAt={policy.lastResolutionAttemptAt}
    />
    <div className="rounded border border-accent/30 bg-accent/5 p-3 text-xs text-muted">
      Counts are configurable for every product and tenor. Nearest-N advances automatically as contracts expire and the catalogue advances;
      All active follows every eligible contract in that tenor. Zero-selected tenors stay off until you enable them.
    </div>
    <label className="relative block">
      <Search size={14} className="absolute left-2 top-2 text-muted" />
      <input value={seriesSearch} onChange={(event) => setSeriesSearch(event.target.value)} placeholder="Search product ladders" className="w-full rounded border border-border bg-transparent py-1.5 pl-7 pr-2 text-sm focus:border-accent focus:outline-none" />
    </label>
    <div className="max-h-[34rem] space-y-2 overflow-auto rounded border border-border p-2">
      {visibleSeries.length === 0 ? <div className="p-3 text-sm text-muted">No product series match this search.</div> : visibleSeries.map((series) => (
        <section key={series.seriesKey} className="rounded border border-border bg-bg">
          <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-border bg-panel/50 px-3 py-2">
            <span className="text-sm font-semibold">{series.seriesLabel}</span>
            <span className="text-[10px] text-muted">{series.commodityCode} · {series.areaCode} · {series.familyCode ?? series.profile ?? "standard"} · {series.currency ?? "—"}/{series.uom ?? "—"}</span>
          </div>
          <div className="grid gap-px bg-border sm:grid-cols-2 xl:grid-cols-3">
            {series.available.map((availability) => {
              const ladder = ladderByKey.get(`${series.seriesKey}\u0000${availability.deliveryKind}`);
              return (
                <div key={availability.deliveryKind} className="space-y-2 bg-bg p-2 text-xs">
                  <label className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2 font-medium"><input type="checkbox" checked={Boolean(ladder)} aria-label={`Enable ${series.seriesLabel} ${availability.deliveryKind}`} onChange={(event) => replaceLadder(series, availability, event.target.checked ? { selectionRule: "nearest_n", targetCount: availability.selectedCount || 1 } : null)} />{availability.deliveryKind}</span>
                    <span className="text-[10px] text-muted">{availability.selectedCount}/{availability.eligibleCount} selected</span>
                  </label>
                  {ladder && <div className="flex items-center gap-2">
                    <select
                      aria-label={`${series.seriesLabel} ${availability.deliveryKind} rule`}
                      value={ladder.selectionRule}
                      onChange={(event) => replaceLadder(series, availability, { selectionRule: event.target.value as "nearest_n" | "all_active", targetCount: ladder.targetCount ?? Math.max(1, availability.selectedCount) })}
                      className="min-w-0 flex-1 rounded border border-border bg-transparent px-1.5 py-1"
                    ><option value="nearest_n">Nearest</option><option value="all_active">All active</option></select>
                    {ladder.selectionRule === "nearest_n" && <input
                      type="number"
                      min={1}
                      max={5_000}
                      value={ladder.targetCount ?? 1}
                      aria-label={`${series.seriesLabel} ${availability.deliveryKind} target count`}
                      onChange={(event) => replaceLadder(series, availability, { selectionRule: "nearest_n", targetCount: Number.parseInt(event.target.value, 10) || 1 })}
                      className="w-20 rounded border border-border bg-transparent px-2 py-1 text-right font-mono"
                    />}
                  </div>}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>

    <details className="rounded border border-border p-3">
      <summary className="cursor-pointer text-sm font-semibold">Contract exceptions <span className="text-xs font-normal text-muted">({overrides.size})</span></summary>
      <div className="mt-3 space-y-2">
        <p className="text-xs text-muted">Always include adds a contract beyond its ladder. Exclude skips it and lets Nearest-N advance to the next eligible contract.</p>
        <label className="relative block"><Search size={14} className="absolute left-2 top-2 text-muted" /><input value={exceptionSearch} onChange={(event) => setExceptionSearch(event.target.value)} placeholder="Search delivery period, product or provider key" className="w-full rounded border border-border bg-transparent py-1.5 pl-7 pr-2 text-sm focus:border-accent focus:outline-none" /></label>
        <div className="max-h-80 overflow-auto rounded border border-border">
          {exceptionItems.length === 0 && unavailableOverrideIds.length === 0
            ? <div className="p-3 text-xs text-muted">{exceptionNeedle ? "No contracts match this search." : "No contract exceptions. Search to add one."}</div>
            : <>
                {unavailableOverrideIds.map((subjectId) => (
                  <div key={subjectId} className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2 last:border-b-0">
                    <div className="min-w-0 text-xs">
                      <span className="block font-medium">Unavailable catalogue contract</span>
                      <span className="block truncate font-mono text-muted">{subjectId}</span>
                    </div>
                    <select
                      value={overrides.get(subjectId)}
                      aria-label={`${subjectId} unavailable contract exception`}
                      onChange={(event) => updateOverride(setOverrides, subjectId, event.target.value)}
                      className="rounded border border-border bg-transparent px-2 py-1 text-xs"
                    ><option value="auto">Remove exception</option><option value="include">Always include</option><option value="exclude">Exclude</option></select>
                  </div>
                ))}
                {exceptionItems.map((item) => {
                  const membership = membershipBySubject.get(item.subjectId);
                  const value = overrides.get(item.subjectId) ?? "auto";
                  return <div key={item.subjectId} className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2 last:border-b-0">
                    <div className="min-w-0 text-xs"><span className="block font-medium">{item.productGroup.label} · {item.deliveryPeriod.label}</span><span className="block truncate text-muted">{item.displayName ?? item.providerKey} · {membership?.selected ? membership.selectionReason ?? "selected" : "not selected"}</span></div>
                    <select
                      value={value}
                      aria-label={`${item.productGroup.label} ${item.deliveryPeriod.label} exception`}
                      onChange={(event) => updateOverride(setOverrides, item.subjectId, event.target.value)}
                      className="rounded border border-border bg-transparent px-2 py-1 text-xs"
                    ><option value="auto">Automatic</option><option value="include">Always include</option><option value="exclude">Exclude</option></select>
                  </div>;
                })}
              </>}
        </div>
        {exceptionNeedle && exceptionItems.length === 200 && <div className="text-[10px] text-muted">Showing the first 200 matches; narrow the search for another contract.</div>}
      </div>
    </details>

    {policy.resolution.deficits.length > 0 && (
      <div className="rounded border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-800 dark:text-amber-200">
        Current catalogue: {policy.resolution.deficits.map((deficit) => `${deficit.deliveryKind} ${deficit.selectedCount}/${deficit.targetCount}`).join(" · ")}. The policy will fill these deficits when eligible contracts appear.
      </div>
    )}
  </div>;
}

const ROLLING_RESOLUTION_FAILURE_COPY: Record<RollingResolutionFailureReason, string> = {
  catalogue_incomplete: "The required catalogue evidence is incomplete.",
  configured_series_unavailable: "A configured product or delivery tenor is no longer available.",
  identity_collision: "The catalogue contains conflicting contract identities and cannot be resolved safely.",
  selection_limit_exceeded: "The resolved universe would exceed the company instrument limit.",
};

export function RollingResolutionWarning({
  strategy,
  resolutionStatus,
  resolutionFailureReason,
  lastResolutionAttemptAt,
}: Pick<EexLiveUniverseRollingPolicyResponse,
  "strategy" | "resolutionStatus" | "resolutionFailureReason" | "lastResolutionAttemptAt">) {
  if (strategy !== "rolling" || resolutionStatus !== "blocked") return null;
  const reason = resolutionFailureReason == null
    ? "The rolling universe could not be resolved safely."
    : ROLLING_RESOLUTION_FAILURE_COPY[resolutionFailureReason];
  return (
    <div role="alert" className="rounded border border-negRed/40 bg-negRed/5 p-3 text-xs text-negRed">
      <div className="font-semibold">Rolling universe update blocked</div>
      <div className="mt-1">{reason}</div>
      <div className="mt-1">
        The last successfully resolved company universe remains active. No partial selection was applied.
      </div>
      {lastResolutionAttemptAt && (
        <div className="mt-1 text-[10px] text-muted">
          Last resolution attempt <time dateTime={lastResolutionAttemptAt}>{new Date(lastResolutionAttemptAt).toLocaleString()}</time>
        </div>
      )}
    </div>
  );
}

function overrideRows(
  overrides: ReadonlyMap<string, EexLiveUniverseRollingOverride["action"]>
): EexLiveUniverseRollingOverride[] {
  return [...overrides.entries()].map(([subjectId, action]) => ({ subjectId, action }));
}

function updateOverride(
  setOverrides: Dispatch<SetStateAction<Map<string, EexLiveUniverseRollingOverride["action"]>>>,
  subjectId: string,
  value: string
) {
  setOverrides((current) => {
    const next = new Map(current);
    if (value === "include" || value === "exclude") next.set(subjectId, value);
    else next.delete(subjectId);
    return next;
  });
}

function groupItems(items: LiveUniverseItem[], search: string): ProductGroup[] {
  const needle = search.trim().toLocaleLowerCase();
  const groups = new Map<string, ProductGroup>();
  for (const item of items) {
    const searchable = [
      item.productGroup.label,
      item.displayName,
      item.providerKey,
      item.commodityCode,
      item.areaCode,
      item.deliveryPeriod.label,
    ].filter(Boolean).join(" ").toLocaleLowerCase();
    if (needle && !searchable.includes(needle)) continue;
    const group = groups.get(item.productGroup.key) ?? {
      key: item.productGroup.key,
      label: item.productGroup.label,
      commodityCode: item.commodityCode,
      areaCode: item.areaCode,
      items: [],
    };
    group.items.push(item);
    groups.set(group.key, group);
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      items: group.items.sort((left, right) => left.deliveryPeriod.sortKey.localeCompare(right.deliveryPeriod.sortKey)),
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
}

function sameSet(left: Set<string>, right: Set<string>): boolean {
  return left.size === right.size && [...left].every((value) => right.has(value));
}
